import { Trans } from '@lingui/react/macro'
import {
  REVISION_OBJECT_STATUSES,
  UNCLASSIFIED_IMPACT_KEY,
} from 'features/knowledgeDocumentViewer/components/revisionCompare/revisionDiffModel'
import { CHANGE_STATUS_COLORS } from 'features/knowledgeDocumentViewer/components/revisionCompare/RevisionDiffOverlay'
import type { RevisionObjectStatus } from 'features/knowledgeDocumentViewer/hooks/useRevisionDiffDetails'
import type { ReactNode } from 'react'
import styled from 'styled-components'

export function revisionStatusLabel(status: RevisionObjectStatus): ReactNode {
  switch (status) {
    case 'added':
      return <Trans>Added</Trans>
    case 'removed':
      return <Trans>Removed</Trans>
    case 'changed':
      return <Trans>Changed</Trans>
    case 'matched':
      return <Trans>Matched</Trans>
  }
}

const Root = styled.div`
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  gap: 0.375rem;
`

const Row = styled.div`
  display: flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
`

const LegendLabel = styled.span`
  color: var(--muted-foreground);
  font-size: 0.75rem;
`

const Swatch = styled.span<{ $color: string }>`
  display: inline-block;
  flex: 0 0 auto;
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 0.125rem;
  background-color: ${(props) => props.$color};
`

const StatusChip = styled.button<{
  $fill: string
  $stroke: string
  $hidden: boolean
}>`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  cursor: pointer;
  border-radius: 0.25rem;
  border: 0.0625rem solid ${(props) => props.$stroke};
  background-color: ${(props) => (props.$hidden ? 'transparent' : props.$fill)};
  padding: 0.0625rem 0.375rem;
  color: var(--foreground);
  font-size: 0.6875rem;
  font-weight: 500;
  white-space: nowrap;
  opacity: ${(props) => (props.$hidden ? 0.5 : 1)};
  text-decoration: ${(props) => (props.$hidden ? 'line-through' : 'none')};
`

const IMPACT_CHIP_FILL = 'var(--muted)'
const IMPACT_CHIP_STROKE = 'var(--border)'

function impactLabel(key: string): ReactNode {
  return key === UNCLASSIFIED_IMPACT_KEY ? <Trans>Unclassified</Trans> : key
}

type RevisionStatusLegendProps = {
  countsByStatus: Record<RevisionObjectStatus, number>
  hiddenStatuses: ReadonlySet<RevisionObjectStatus>
  onToggle: (status: RevisionObjectStatus) => void
  /** Drawn-box counts keyed by producer impact; `''` is the unclassified bucket. */
  countsByImpact: ReadonlyMap<string, number>
  hiddenImpacts: ReadonlySet<string>
  onToggleImpact: (impact: string) => void
}

/**
 * Status chips that double as visibility toggles for the overlay and the
 * event list. A hidden status stays listed (struck through) so it can be
 * turned back on. A second row filters by the producer's impact classes,
 * derived from the data, and appears only when some object carries one.
 */
export function RevisionStatusLegend({
  countsByStatus,
  hiddenStatuses,
  onToggle,
  countsByImpact,
  hiddenImpacts,
  onToggleImpact,
}: RevisionStatusLegendProps) {
  const hasClassifiedImpact = Array.from(countsByImpact.keys()).some(
    (key) => key !== UNCLASSIFIED_IMPACT_KEY
  )
  return (
    <Root>
      <Row>
        <LegendLabel>
          <Trans>Show</Trans>
        </LegendLabel>
        {REVISION_OBJECT_STATUSES.map((status) => {
          const colors = CHANGE_STATUS_COLORS[status]
          const hidden = hiddenStatuses.has(status)
          return (
            <StatusChip
              key={status}
              type='button'
              aria-pressed={!hidden}
              $fill={colors.fill}
              $stroke={colors.stroke}
              $hidden={hidden}
              onClick={() => onToggle(status)}>
              <Swatch $color={colors.stroke} />
              {revisionStatusLabel(status)} {countsByStatus[status]}
            </StatusChip>
          )
        })}
      </Row>
      {hasClassifiedImpact ? (
        <Row>
          <LegendLabel>
            <Trans>Impact</Trans>
          </LegendLabel>
          {Array.from(countsByImpact, ([key, count]) => {
            const hidden = hiddenImpacts.has(key)
            return (
              <StatusChip
                key={key}
                type='button'
                aria-pressed={!hidden}
                $fill={IMPACT_CHIP_FILL}
                $stroke={IMPACT_CHIP_STROKE}
                $hidden={hidden}
                onClick={() => onToggleImpact(key)}>
                {impactLabel(key)} {count}
              </StatusChip>
            )
          })}
        </Row>
      ) : null}
    </Root>
  )
}
